import { ProceduralSoundGenerator } from '@audio/ProceduralSoundGenerator';

export enum SoundType {
  // Weapon sounds
  STANDARD_WEAPON = 'standard_weapon',
  RAILGUN_CHARGE = 'railgun_charge',
  RAILGUN_FIRE = 'railgun_fire',
  TORPEDO_LAUNCH = 'torpedo_launch',
  POINT_DEFENSE = 'point_defense',
  
  // Impact sounds
  SHIELD_IMPACT_LIGHT = 'shield_impact_light',
  SHIELD_IMPACT_HEAVY = 'shield_impact_heavy',
  HULL_IMPACT_LIGHT = 'hull_impact_light',
  HULL_IMPACT_HEAVY = 'hull_impact_heavy',
  
  // Explosion sounds
  EXPLOSION_SMALL = 'explosion_small',
  EXPLOSION_LARGE = 'explosion_large',

  // Mothership sounds
  SHIP_ASSEMBLE = 'ship_assemble',
  REPAIR_WELD = 'repair_weld',
  DEPLOY_CONFIRM = 'deploy_confirm',
  DEPLOY_RECALL = 'deploy_recall',
  LAUNCH = 'launch',
  UI_DENY = 'ui_deny',
  CREDITS_TICK = 'credits_tick',
}

export class SoundManager {
  private static instance: SoundManager;
  private generator: ProceduralSoundGenerator;
  private sounds: Map<SoundType, AudioBuffer> = new Map();
  private enabled: boolean = true;
  private volume: number = 0.5;
  private initialized: boolean = false;

  private constructor() {
    this.generator = new ProceduralSoundGenerator();
  }

  static getInstance(): SoundManager {
    if (!SoundManager.instance) {
      SoundManager.instance = new SoundManager();
    }
    return SoundManager.instance;
  }

  async initialize(): Promise<void> {
    if (this.initialized) return;

    // Ensure audio context is ready
    await this.generator.ensureAudioContext();

    // Generate all sounds
    this.generateAllSounds();
    this.initialized = true;
  }

  private generateAllSounds(): void {
    // Standard weapon sound - short laser burst
    this.sounds.set(
      SoundType.STANDARD_WEAPON,
      this.generator.generateWeaponSound({
        type: 'laser',
        seed: 12345,
        duration: 0.15,
        pitch: 1000,
        modulation: 30
      })
    );

    // Railgun charge sound
    this.sounds.set(
      SoundType.RAILGUN_CHARGE,
      this.generator.generateWeaponSound({
        type: 'railgun',
        seed: 23456,
        duration: 1.0
      })
    );

    // Railgun fire sound (just the discharge part)
    this.sounds.set(
      SoundType.RAILGUN_FIRE,
      this.generator.generateWeaponSound({
        type: 'railgun',
        seed: 23457,
        duration: 0.3
      })
    );

    // Torpedo launch sound
    this.sounds.set(
      SoundType.TORPEDO_LAUNCH,
      this.generator.generateWeaponSound({
        type: 'torpedo',
        seed: 34567,
        duration: 0.5
      })
    );

    // Point defense sound - rapid fire, short burst
    this.sounds.set(
      SoundType.POINT_DEFENSE,
      this.generator.generateWeaponSound({
        type: 'laser',
        seed: 98765,
        duration: 0.05, // Very short for rapid fire
        pitch: 2000, // Higher pitch for smaller weapon
        modulation: 50 // More modulation for distinctive sound
      })
    );

    // Shield impact sounds
    this.sounds.set(
      SoundType.SHIELD_IMPACT_LIGHT,
      this.generator.generateImpactSound({
        type: 'shield',
        seed: 45678,
        duration: 0.2,
        intensity: 0.5
      })
    );

    this.sounds.set(
      SoundType.SHIELD_IMPACT_HEAVY,
      this.generator.generateImpactSound({
        type: 'shield',
        seed: 45679,
        duration: 0.3,
        intensity: 1.0
      })
    );

    // Hull impact sounds
    this.sounds.set(
      SoundType.HULL_IMPACT_LIGHT,
      this.generator.generateImpactSound({
        type: 'hull',
        seed: 56789,
        duration: 0.25,
        intensity: 0.5
      })
    );

    this.sounds.set(
      SoundType.HULL_IMPACT_HEAVY,
      this.generator.generateImpactSound({
        type: 'hull',
        seed: 56790,
        duration: 0.35,
        intensity: 1.0
      })
    );

    // Explosion sounds (combine multiple techniques)
    this.sounds.set(
      SoundType.EXPLOSION_SMALL,
      this.generateExplosionSound(67890, 0.4, 0.6)
    );

    this.sounds.set(
      SoundType.EXPLOSION_LARGE,
      this.generateExplosionSound(67891, 0.8, 1.0)
    );

    // Mothership sounds
    this.sounds.set(SoundType.SHIP_ASSEMBLE, this.generator.generateBaseSound({ type: 'warp', seed: 71001, duration: 1.1 }));
    this.sounds.set(SoundType.REPAIR_WELD, this.generator.generateBaseSound({ type: 'weld', seed: 71002, duration: 0.9 }));
    this.sounds.set(SoundType.DEPLOY_CONFIRM, this.generator.generateBaseSound({ type: 'blip', seed: 71003, duration: 0.18, pitch: 660 }));
    this.sounds.set(SoundType.DEPLOY_RECALL, this.generator.generateBaseSound({ type: 'blip', seed: 71004, duration: 0.18, pitch: -520 }));
    this.sounds.set(SoundType.LAUNCH, this.generator.generateBaseSound({ type: 'launch', seed: 71005, duration: 1.6 }));
    this.sounds.set(SoundType.UI_DENY, this.generator.generateBaseSound({ type: 'deny', seed: 71006, duration: 0.25 }));
    this.sounds.set(SoundType.CREDITS_TICK, this.generator.generateBaseSound({ type: 'chime', seed: 71007, duration: 0.12, pitch: 1760 }));
  }

  private generateExplosionSound(seed: number, duration: number, intensity: number): AudioBuffer {
    // Combine impact and weapon sounds for explosion effect
    const hullImpact = this.generator.generateImpactSound({
      type: 'hull',
      seed: seed,
      duration: duration,
      intensity: intensity
    });

    // For now, return the hull impact as explosion
    // In a more complex implementation, we could mix multiple sounds
    return hullImpact;
  }

  playSound(soundType: SoundType, volume?: number): void {
    if (!this.enabled || !this.initialized) return;

    const sound = this.sounds.get(soundType);
    if (!sound) {
      console.warn(`Sound not found: ${soundType}`);
      return;
    }

    const finalVolume = (volume ?? 1.0) * this.volume;
    this.generator.playSound(sound, finalVolume);
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
  }

  getVolume(): number {
    return this.volume;
  }

  isEnabled(): boolean {
    return this.enabled;
  }
}